import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchStudentSubjectsWithStudents, fetchMessagingSummary } from '../../services/api';
import { useUnreadMessagingSummary } from '../../utils/messageReadState';

export default function StudentHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [rawMessagingSummary, setMessagingSummary] = useState<any>(null);
  const messagingSummary = useUnreadMessagingSummary(rawMessagingSummary, user);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    fetchStudentSubjectsWithStudents()
      .then(data => {
        if (mounted) setSubjects(data.subjects || []);
      })
      .catch(() => {
        if (mounted) setError('Could not load subjects/classes/students.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    if (typeof fetchMessagingSummary === 'function') {
      fetchMessagingSummary()
        .then(data => {
          if (mounted) setMessagingSummary(data);
        })
        .catch(() => {
          if (mounted) setMessagingSummary(null);
        });
    }

    return () => {
      mounted = false;
    };
  }, []);

  const userName = user?.first_name
    ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`
    : (user?.email || '');

  return (
    <div>
      <div className="ae-page-hero">
        <h1>{`Welcome${userName ? ', ' + userName : ''}!`}</h1>
        <p>Here's a quick look at your classes and shortcuts to your most-used tools.</p>
      </div>

      {/* Quick-access tiles */}
      <div className="ae-tile-grid" style={{ marginBottom: 28 }}>
        {[
          { icon: '📝', label: 'My Grades', desc: 'View your current grades and standards progress', path: '/student/grades' },
          { icon: '✅', label: 'To Do', desc: 'Check tasks and assignments from your teachers', path: '/student/todo' },
          { icon: '📎', label: 'Action Cards', desc: 'Open activity resources shared by your school', path: '/student/action-cards' },
          { icon: '💬', label: 'Feedback', desc: 'Read feedback sent by your teachers', path: '/student/feedback' },
        ].map(({ icon, label, desc, path }) => (
          <button key={path} onClick={() => navigate(path)} className="ae-tile">
            <span className="ae-tile-icon">{icon}</span>
            <span className="ae-tile-title">{label}</span>
            <span className="ae-tile-desc">{desc}</span>
          </button>
        ))}
      </div>

      {/* Messages Spot on Student Dashboard */}
      {messagingSummary && messagingSummary.incoming_count > 0 && (
        <section
          aria-label="New Messages"
          style={{
            background: '#f0f7ff',
            border: '1.5px solid #93c5fd',
            borderRadius: 14,
            padding: '16px 20px',
            marginBottom: 24,
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.08)',
          }}
          data-testid="student-dashboard-messages-spot"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#dbeafe', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                💬
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h2 style={{ fontSize: 16, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>
                    New Messages
                  </h2>
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#2563eb', color: '#ffffff', borderRadius: 999, padding: '2px 8px' }}>
                    {messagingSummary.incoming_count} {messagingSummary.incoming_count === 1 ? 'new' : 'new'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Recent messages from your teachers or family.
                </div>
              </div>
            </div>
            <button
              type="button"
              style={{ background: '#2563eb', color: '#ffffff', border: '1px solid #1d4ed8', borderRadius: 10, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              onClick={() => navigate('/student/connect')}
            >
              Open Messages →
            </button>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            {(messagingSummary.recent_messages || []).slice(0, 3).map((msg) => (
              <div
                key={msg.message_id}
                role="button"
                tabIndex={0}
                style={{
                  background: '#ffffff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 10,
                  padding: '10px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  gap: 12,
                }}
                onClick={() => navigate(`/student/connect?participant_id=${msg.sender_id}&student_id=${msg.student_id || ''}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/student/connect?participant_id=${msg.sender_id}&student_id=${msg.student_id || ''}`);
                  }
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>{msg.sender_name}</span>
                    {msg.sent_at && (
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {new Date(msg.sent_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}{' '}
                        {new Date(msg.sent_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    &ldquo;{msg.body}&rdquo;
                  </div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>
                  Reply →
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <div>Loading classes...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {subjects.length === 0 && <div style={{ color: 'var(--ae-text-muted)' }}>No subjects found yet. Your teacher will add you to classes soon.</div>}
          {subjects.map(subject => (
            <section key={subject.subject_id}>
              <div style={{ marginBottom: 10 }}>
                <span className="ae-pill">📘 {subject.subject_name}</span>
              </div>
              {subject.classes.length === 0 ? (
                <div style={{ marginLeft: 12, color: 'var(--ae-text-muted)' }}>No classes for this subject.</div>
              ) : (
                <div className="ae-tile-grid">
                  {subject.classes.map(cls => (
                    <article key={cls.class_id} className="ae-tile">
                      <p className="ae-tile-title">
                        {cls.class_name ? cls.class_name : `Class ${cls.class_id}`}
                      </p>
                      <p className="ae-tile-desc">Class ID: {cls.class_id}</p>
                      <span className="ae-pill">👥 {(cls.students || []).length} students</span>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
